import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, Circle, Plus } from 'lucide-react';
import { LoanOfficer } from '../data/mockData';
import { formatCurrency, formatPercent } from '../utils/helpers';
interface CoachingPanelProps {
  selectedLO: LoanOfficer | null;
  isOpen: boolean;
  onClose: () => void;
}
interface Task {
  id: string;
  text: string;
  completed: boolean;
}
export function CoachingPanel({
  selectedLO,
  isOpen,
  onClose
}: CoachingPanelProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskText, setNewTaskText] = useState('');
  // Reset tasks when LO changes
  useEffect(() => {
    if (selectedLO) {
      const initialTasks = selectedLO.coachingFlags.map((flag, idx) => ({
        id: `task-${idx}`,
        text: `Address: ${flag.description}`,
        completed: false
      }));
      if (initialTasks.length === 0) {
        initialTasks.push({
          id: 'task-default',
          text: 'Review pipeline health and transfer quality',
          completed: false
        });
      }
      setTasks(initialTasks);
    }
  }, [selectedLO]);
  if (!isOpen || !selectedLO) return null;
  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskText.trim()) return;
    setTasks([...tasks, {
      id: Date.now().toString(),
      text: newTaskText,
      completed: false
    }]);
    setNewTaskText('');
  };
  const toggleTask = (id: string) => {
    setTasks(tasks.map(t => t.id === id ? {
      ...t,
      completed: !t.completed
    } : t));
  };
  const generateAgenda = () => {
    if (selectedLO.coachingFlags.length === 0) {
      return ['Review recent wins and strong performance areas', 'Discuss goals for next quarter', 'Identify any roadblocks or support needed'];
    }
    return selectedLO.coachingFlags.map(flag => {
      if (flag.type === 'low-calls') return 'Review call activity trends and time management';
      if (flag.type === 'low-transfers') return 'Discuss strategies to improve call transfer rates';
      if (flag.type === 'high-failed-transfers') return 'Analyze recent failed transfers and prevention';
      if (flag.type === 'stale-pipeline') return 'Review suspended files and unblock stale deals';
      return `Discuss ${flag.type.replace('-', ' ')}`;
    });
  };
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity" onClick={onClose} data-id="element-37" />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-[420px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out translate-x-0 overflow-y-auto" data-id="element-38">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-start sticky top-0 bg-white z-10" data-id="element-39">
          <div data-id="element-40">
            <div className="flex items-center gap-3 mb-1" data-id="element-41">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg" data-id="element-42">
                {selectedLO.initials}
              </div>
              <div data-id="element-43">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-44">
                  {selectedLO.name}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-45">
                  {selectedLO.team} • {selectedLO.roleStage}
                </p>
              </div>
            </div>
            {selectedLO.coachingFlags.length > 0 ? <span className="inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-46">
                Needs Coaching
              </span> : <span className="inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-47">
                Strong Performance
              </span>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-48">
            <X className="w-5 h-5" data-id="element-49" />
          </button>
        </div>

        <div className="p-6 flex-1 flex flex-col gap-8" data-id="element-50">
          {/* Quick Metrics */}
          <div className="grid grid-cols-2 gap-4" data-id="element-51">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-52">
              <p className="text-xs text-gray-500 mb-1" data-id="element-53">Active Pipeline</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-54">
                {formatCurrency(selectedLO.activePipelineValue)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-55">
              <p className="text-xs text-gray-500 mb-1" data-id="element-56">Transfer Rate</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-57">
                {formatPercent(selectedLO.transferRate)}
              </p>
            </div>
          </div>

          {/* Flag Summary */}
          {selectedLO.coachingFlags.length > 0 && <section data-id="element-58">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-59">
                Active Flags
              </h3>
              <div className="space-y-3" data-id="element-60">
                {selectedLO.coachingFlags.map(flag => <div key={flag.id} className="flex items-start gap-3 bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-61">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-62" />
                    <p className="text-sm text-red-900" data-id="element-63">{flag.description}</p>
                  </div>)}
              </div>
            </section>}

          {/* Suggested Agenda */}
          <section data-id="element-64">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-65">
              Suggested 1:1 Agenda
            </h3>
            <ul className="space-y-2" data-id="element-66">
              {generateAgenda().map((item, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-67">
                  <span className="text-blue-500 mt-0.5" data-id="element-68">•</span>
                  {item}
                </li>)}
            </ul>
          </section>

          {/* Coaching Tasks */}
          <section data-id="element-69">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-70">
              Coaching Tasks
            </h3>
            <div className="space-y-2 mb-4" data-id="element-71">
              {tasks.map(task => <div key={task.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" onClick={() => toggleTask(task.id)} data-id="element-72">
                  {task.completed ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-73" /> : <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" data-id="element-74" />}
                  <span className={`text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`} data-id="element-75">
                    {task.text}
                  </span>
                </div>)}
            </div>

            <form onSubmit={handleAddTask} className="flex gap-2" data-id="element-76">
              <input type="text" value={newTaskText} onChange={e => setNewTaskText(e.target.value)} placeholder="Add a new task..." className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-77" />
              <button type="submit" disabled={!newTaskText.trim()} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-78">
                <Plus className="w-5 h-5" data-id="element-79" />
              </button>
            </form>
          </section>
        </div>
      </div>
    </>;
}